import { FilterPointsDirectory } from '@/infrastructure/Configuration'
import { getQueryParams } from '@/infrastructure/Utils'
import { corporateRoles } from '@/domain/MyAccount'
import { DateFactory } from 'rhg-vue-common'

const eqCode = left => right => left.code === right.code

export default {
  getCurrentCustomer: state => state.currentCustomer,
  isDateRangeSelected: state => state.checkInDate !== null && state.checkOutDate !== null,
  isSomeBrandSelected: state => state.selectedBrands.length !== 0,
  isSomeHotelTypeSelected: state => state.selectedHotelTypes.length !== 0,
  isSomeFilterSelected: (state, getters) =>
    Boolean(
      getters.isSomeBrandSelected ||
        getters.isSomeHotelTypeSelected ||
        getters.isDateRangeSelected ||
        state.isOnlyRewards ||
        state.selectedOfferCountry ||
        state.selectedOfferCity ||
        state.directory.selectedCountry ||
        state.directory.points.min !== FilterPointsDirectory.MIN ||
          state.directory.points.max !== FilterPointsDirectory.MAX
    ),
  isSelectedBrand: state => brandCode =>
    state.selectedBrands.some(brand => brand.code === brandCode),
  isSelectedHotelType: state => hotelTypeCode =>
    state.selectedHotelTypes.some(hotelType => hotelType.code === hotelTypeCode),
  isSelectedHotelTypes: state => hotel =>
    state.selectedHotelTypes.every(hotelType => hotel.types
      ? hotel.types.includes(hotelType.code)
      : hotel.type === hotelType.code
    ),
  hotels: (state, getters) => {
    let hotels = state.hotels
    if (getters.isSomeBrandSelected) {
      hotels = hotels.filter(hotel => getters.isSelectedBrand(hotel.brand))
    }
    if (getters.isSomeHotelTypeSelected) {
      hotels = hotels.filter(hotel =>
        getters.isSelectedHotelTypes(hotel)
      )
    }
    return hotels
  },
  tripAdvisorRatingForHotel: state => code =>
    state.tripAdvisorRating.find(rating => rating.code === code),

  brands: state => {
    const brandCodes = state.hotels
      .map(hotel => hotel.brand)
      .concat(state.offers.reduce((acc, offer) => acc.concat(offer.brands), []))
    return state.brands.filter(brand => brandCodes.some(brandCode => brand.code === brandCode))
  },
  hotelTypes: state => {
    const hotelTypes = Array.from(
      new Set(
        state.hotels
          .reduce((acc, hotel) => acc.concat(hotel.types || hotel.type), [])
          .filter(type => type)
      )
    )
    return state.hotelTypes.filter(hotelType =>
      hotelTypes.some(hotelTypeCode => hotelType.code === hotelTypeCode)
    )
  },
  offers: (state, getters) => {
    let offers = state.offers

    if (getters.isSomeBrandSelected) {
      offers = offers.filter(offer =>
        offer.brands.some(brandCode => getters.isSelectedBrand(brandCode))
      )
    }

    if (getters.isSomeHotelTypeSelected) {
      offers = offers.filter(offer =>
        offer.categories.some(hotelTypeCode => getters.isSelectedHotelType(hotelTypeCode))
      )
    }

    if (state.isOnlyRewards) {
      offers = offers.filter(offer => offer.rewards)
    }

    if (state.selectedOfferCountry) {
      offers = offers.filter(offer => offer.countries.some(eqCode(state.selectedOfferCountry)))
    }

    if (getters.isDateRangeSelected) {
      offers = offers.filter(
        offer =>
          offer.bookingStartDate < state.checkOutDate && offer.bookingEndDate > state.checkInDate
      )
    }

    if (state.selectedOfferCity) {
      offers = offers.filter(offer =>
        offer.countries
          .filter(eqCode(state.selectedOfferCountry))
          .some(country => country.cities.some(eqCode(state.selectedOfferCity)))
      )
    }

    return offers.sort((offerA, offerB) => offerA.priority - offerB.priority)
  },
  directoryHotels: state => {
    return state.directory.hotels
  },
  directoryCountries: state => {
    return state.directory.countries
  },
  directoryCities: state => {
    return state.directory.cities
  },
  directoryStates: state => {
    return state.directory.states
  },
  directoryTotalResults: state => {
    if (state.directory.pagination.total === null) {
      return '0'
    } else {
      return state.directory.pagination.total
    }
  },

  checkInDate: state => state.checkInDate && DateFactory.formatDate(state.checkInDate),

  checkOutDate: state => state.checkOutDate && DateFactory.formatDate(state.checkOutDate),

  getUrlParams: () => () => getQueryParams(window.location.search),

  userCountry: () => {
    const country = global.AkamaiService && global.AkamaiService.userCountry
      ? global.AkamaiService.userCountry()
      : 'ES'
    return country || 'ES'
  },

  customerProfile: state => state.customerProfile,
  loadingCustomer: state => state.loadingCustomer,
  preferences: state => state.preferences,
  loadingPreferences: state => state.loadingPreferences,
  loadingNewsletter: state => state.loadingNewsletter,
  getCustomerSummary: state => state.customerSummary,
  currentModalId: state => state.currentModalId,
  isFormEdited: state => state.isFormEdited,
  isChangeDiscarded: state => state.isChangeDiscarded,
  isNewCorporateProgramActive: state => Object.keys(state.currentCustomer).length !== 0 && state.currentCustomer.programType === 'CORP',
  isCorporateBooker: (state, getters) => getters.isNewCorporateProgramActive && state.currentCustomer.memberType.toLowerCase() === corporateRoles.BOOKER,
  isCorporateAdminOrDeputy: (state, getters) => getters.isNewCorporateProgramActive &&
    [corporateRoles.ADMINISTRATOR, corporateRoles.DEPUTY].includes(state.currentCustomer.memberType.toLowerCase()),
  isReservationFilterVisible: (state, getters) => (getters.isCorporateAdminOrDeputy || (getters.isCorporateBooker && state.reservations.memberType !== 'personal')) &&
   state.reservations.groupType === 'future',
  multiFactor: state => Object.keys(state.customerProfile).length !== 0 && state.customerProfile.multifactor,
  telephone: state => Object.keys(state.customerProfile).length !== 0 && state.customerProfile.telephone,
  transcationsFilterApplied: state => state.transactions?.filterApplied,
  isFilterSelectedClear: state => Object.values(state.transactions?.filters).every(val => val === ''),
  loadingClaimPoints: state => state.loadingClaimPoints
}
